import { Button, Grid, Group, Stack, Text, Textarea, Title } from '@mantine/core';
import _ from 'lodash';
import moment from 'moment';
import { useState } from 'react';

import { EORIGIN_DATE_FORMAT_WITH_TIME, isDeparture, NCTSMessageType, Transit, TransitStatus } from '@e-origin/shared';

import { useInvalidateTransit } from '@hooks/transit';

// check if the invalidation request button should be displayed:
// - if the transit is accepted or guarantee not valid
// - if the invalidation request is not already sent
// - if the invalidation decision is negative - allow to resend the invalidation request
const canDisplayRequestInvalidationButton = (transit: Transit): boolean =>
  ([TransitStatus.ACCEPTED, TransitStatus.GUARANTEE_NOT_VALID].includes(transit.generalInfo.status) &&
    !transit.customsState?.invalidationRequest?.invalidationRequestDate) ||
  transit.customsState?.invalidationDecision?.decision === '0';

interface InvalidationSectionProps {
  transit: Transit;
}

export const InvalidationSection = ({ transit: transitObj }: InvalidationSectionProps) => {
  const transit = _.cloneDeep(transitObj);
  const transitIsDeparture = isDeparture(transit);

  const invalidateTransitMutation = useInvalidateTransit();

  const [invalidationRequestReason, setInvalidationRequestReason] = useState(transit?.customsState?.invalidationRequest?.invalidationReason || '');

  const handleInvalidationReasonChange = (e: any) => {
    setInvalidationRequestReason(e.target.value);
  };

  const handleSendInvalidationRequest = () => {
    invalidateTransitMutation.mutate({ id: transit._id, reason: invalidationRequestReason });
  };

  // errors - if invalidation form is sent and customs sends back errors
  const errors = transit.customsState?.functionalError?.filter((error) =>
    error.errorPointer.toLocaleLowerCase().includes(NCTSMessageType.CC014C.toLocaleLowerCase()),
  );

  return (
    <Stack gap="xl" mb="md">
      {/** invalidation info */}
      {transitIsDeparture && [TransitStatus.ACCEPTED, TransitStatus.GUARANTEE_NOT_VALID].includes(transit.generalInfo?.status) && (
        <Stack>
          <Title order={5} fw={600}>
            Invalidation
          </Title>
          <Stack gap="xl">
            {errors?.length > 0 &&
              errors.map((error, idx) => (
                <Text key={idx}>
                  Error pointer: {error.errorPointer} (Error code: {error.errorCode}) {error.errorReason} {error.originalAttributeValue}
                </Text>
              ))}
            <Grid>
              <Grid.Col span={5} mr="md">
                <Stack>
                  <Text c="gray" size="sm">
                    Invalidation reason
                  </Text>
                  <Textarea
                    resize="vertical"
                    value={invalidationRequestReason}
                    onChange={handleInvalidationReasonChange}
                    disabled={
                      // if invalidation request is already sent or customs declined the invalidation (allow to resend the invalidation request)
                      !!transit.customsState?.invalidationRequest?.invalidationRequestDate && transit.customsState?.invalidationDecision?.decision !== '0'
                    }
                  />
                  {canDisplayRequestInvalidationButton(transit) && (
                    <Group>
                      <Button type="button" disabled={!invalidationRequestReason.length} onClick={handleSendInvalidationRequest} variant="filled" size="lg">
                        Request invalidation
                      </Button>
                    </Group>
                  )}
                </Stack>
              </Grid.Col>

              {!!transit.customsState?.invalidationRequest?.invalidationRequestDate && (
                <Grid.Col span={3}>
                  <Stack gap="xs">
                    <Text c="gray" size="sm">
                      Invalidation request date
                    </Text>
                    <Text fw={500}>{moment(transit.customsState?.invalidationRequest?.invalidationRequestDate).format('YYYY-MM-DD')}</Text>
                  </Stack>
                </Grid.Col>
              )}
            </Grid>
          </Stack>
        </Stack>
      )}
      {/** /invalidation info */}
      {/** invalidation decision */}
      {transit.customsState?.invalidationDecision && (
        <Stack>
          <Grid>
            <Grid.Col span={3}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Invalidation decision date
                </Text>
                <Text fw={500}>{moment(transit.customsState?.invalidationDecision?.invalidationDecisionDate).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={3}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Invalidation request date
                </Text>
                <Text fw={500}>{moment(transit.customsState?.invalidationRequest?.invalidationRequestDate).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={3}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Invalidation initiated by customs
                </Text>
                <Text fw={500}>{Number(transit.customsState?.invalidationDecision?.invalidationInitiatedByCustoms) === 1 ? 'yes' : 'no'}</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={3}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Invalidation decision reason
                </Text>
                <Text fw={500}>{transit.customsState?.invalidationDecision?.invalidationReason}</Text>
              </Stack>
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Invalidation reason
                </Text>
                <Textarea
                  value={transit.customsState?.invalidationDecision?.invalidationReason ?? transit.customsState?.invalidationRequest?.invalidationReason}
                  disabled
                  autosize
                />
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      )}
      {/** /invalidation decision */}
    </Stack>
  );
};
